// src/Hooks/useBroadcastManage.js

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  doc,
  writeBatch,
  getDoc,
  getDocs,
  updateDoc,
  increment,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../Firebase/firebase';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { getPortfolioEmailContent } from '../Components/Promo/PortfolioEmail';

export function useBroadcastManage() {
  const [loading, setLoading] = useState(true);

  // Page 1 selection state
  const [selectedIds, setSelectedIds] = useState([]);

  // Page 2 specific state
  const [page2SelectedIds, setPage2SelectedIds] = useState([]);
  const [page2SortConfig, setPage2SortConfig] = useState({
    key: 'addedAt',
    direction: 'desc',
  });

  const [dbRows, setDbRows] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [edits, setEdits] = useState({});

  const [newsfeedOptions, setNewsfeedOptions] = useState([]);

  // State to hold the history of imported broadcast campaigns
  const [broadcastHistory, setBroadcastHistory] = useState([]);

  // AMENDED: Converted from a Map() to a standard Object {} to guarantee React re-renders
  const [unsubscribedEmails, setUnsubscribedEmails] = useState({});

  const [delayConfigString, setDelayConfigString] = useState('48:00:00');

  const [inProgressCampaigns, setInProgressCampaigns] = useState({});

  const [deleteTarget, setDeleteTarget] = useState('PAGE1');

  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [showBroadcastConfirm, setShowBroadcastConfirm] = useState(false);
  const [broadcastDetails, setBroadcastDetails] = useState({
    targetCount: 0,
    newsfeedName: '',
    isImportList: false, // Tracks if this broadcast includes the import holding pen
  });

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [showProgressAlert, setShowProgressAlert] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressSeverity, setProgressSeverity] = useState('info');

  const [textFormatting, setTextFormatting] = useState({ makeUppercase: false });

  useEffect(() => {
    // 1. Listen to broadcastCampaigns
    const unsubBroadcasts = onSnapshot(
      collection(db, 'broadcastCampaigns'),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // Sort ascending by date
        fetchedData.sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateA - dateB;
        });

        setDbRows(fetchedData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching broadcastCampaigns:', error);
        setLoading(false);
      },
    );

    // 2. Listen to newsPortfolio to populate the dropdown
    const unsubNewsfeed = onSnapshot(
      collection(db, 'newsPortfolio'),
      (snapshot) => {
        let fetchedFeeds = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        fetchedFeeds.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setNewsfeedOptions(fetchedFeeds);
      },
      (error) => {
        console.error('Error fetching newsPortfolio:', error);
      },
    );

    // 3. Listen to uploadMailingListBroadcast
    const unsubHistory = onSnapshot(
      collection(db, 'uploadMailingListBroadcast'),
      (snapshot) => {
        const fetched = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setBroadcastHistory(fetched);
      },
      (error) => {
        console.error('Error fetching uploadMailingListBroadcast:', error);
      },
    );

    // 4. Real-time listener for unsubscribeRequests to act as Single Source of Truth
    const unsubUnsubscribeRequests = onSnapshot(
      collection(db, 'unsubscribeRequests'),
      (snapshot) => {
        const emailsObj = {}; // AMENDED: Using a standard object instead of Map
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.email) {
            emailsObj[data.email.toLowerCase().trim()] = data.status || 'Unsubscribed';
          }
        });
        setUnsubscribedEmails(emailsObj);
      },
      (error) => {
        console.error('Error fetching unsubscribeRequests:', error);
      },
    );

    const unsubDelayConfig = onSnapshot(
      doc(db, 'settings', 'BroadcastDelay'),
      (snapshot) => {
        if (snapshot.exists() && snapshot.data().delay) {
          setDelayConfigString(snapshot.data().delay);
        }
      },
      (error) => {
        console.error(
          'Error fetching dynamic structural BroadcastDelay config doc:',
          error,
        );
      },
    );

    const unsubFormat = onSnapshot(
      doc(db, 'settings', 'TextFormatting'),
      (docSnap) => {
        if (docSnap.exists()) {
          setTextFormatting({ makeUppercase: docSnap.data().makeUppercase === true });
        } else {
          setTextFormatting({ makeUppercase: false });
        }
      },
      (error) => console.error('Error fetching TextFormatting:', error),
    );

    return () => {
      unsubBroadcasts();
      unsubNewsfeed();
      unsubHistory();
      unsubUnsubscribeRequests();
      unsubDelayConfig();
      unsubFormat();
    };
  }, []);

  const displayRows = useMemo(() => {
    const mergedDbRows = dbRows.map((row) =>
      edits[row.id] ? { ...row, ...edits[row.id] } : row,
    );
    const newRowIds = new Set(newRows.map((r) => r.id));
    const combinedRows = [
      ...mergedDbRows.filter((r) => !newRowIds.has(r.id)),
      ...newRows,
    ];

    return combinedRows.map((row) => {
      if (inProgressCampaigns[row.id]) {
        return {
          ...row,
          status: 'In Process',
          date: inProgressCampaigns[row.id],
        };
      }
      return row;
    });
  }, [dbRows, edits, newRows, inProgressCampaigns]);

  // Page 2 Sorting Logic
  const displayPage2Rows = useMemo(() => {
    const errorStatuses = [
      'Unsubscribed',
      'Bounce Back',
      'Hard Bounce',
      'Soft Bounce',
      'Spam Complaint',
    ];

    let rows = broadcastHistory.map((row) => {
      if (row.email) {
        const cleanEmail = String(row.email).toLowerCase().trim();

        // AMENDED: Directly checks the object property to trigger reliable React updates
        if (unsubscribedEmails[cleanEmail]) {
          return { ...row, status: unsubscribedEmails[cleanEmail] };
        } else if (errorStatuses.includes(row.status)) {
          return { ...row, status: 'Ok' };
        }
      }
      return row;
    });

    if (page2SortConfig.key) {
      rows.sort((a, b) => {
        let valA = a[page2SortConfig.key] || '';
        let valB = b[page2SortConfig.key] || '';

        if (page2SortConfig.key === 'addedAt') {
          valA = a.addedAt || a.requestDate || '';
          valB = b.addedAt || b.requestDate || '';
          if (!valA && valB) return page2SortConfig.direction === 'asc' ? 1 : -1;
          if (valA && !valB) return page2SortConfig.direction === 'asc' ? -1 : 1;
          if (!valA && !valB) return 0;

          valA = new Date(valA).getTime();
          valB = new Date(valB).getTime();
          if (valA < valB) return page2SortConfig.direction === 'asc' ? -1 : 1;
          if (valA > valB) return page2SortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }

        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();

        if (valA < valB) return page2SortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return page2SortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [broadcastHistory, page2SortConfig, unsubscribedEmails]);

  const requestPage2Sort = (key) => {
    let direction = 'asc';
    if (page2SortConfig.key === key && page2SortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setPage2SortConfig({ key, direction });
  };

  const checkIsDirty = () => {
    return newRows.length > 0 || Object.keys(edits).length > 0;
  };

  const checkCanBroadcast = () => {
    if (checkIsDirty()) return false;
    if (selectedIds.length !== 1) return false;
    const selectedRow = displayRows.find((r) => r.id === selectedIds[0]);
    if (!selectedRow) return false;
    return !selectedRow.status || selectedRow.status.trim() === '';
  };

  const handleAddNew = () => {
    const newId = `temp_${Date.now().toString()}`;
    setNewRows((prev) => [
      ...prev,
      {
        id: newId,
        date: new Date().toISOString(),
        description: '',
        newsfeedName: '',
        isImportList: false,
        isMailingList: false,
        status: '',
      },
    ]);
  };

  const handleDeleteRequest = (target = 'PAGE1') => {
    if (target === 'PAGE1' && selectedIds.length === 0) return;
    if (target === 'PAGE2' && page2SelectedIds.length === 0) return;

    setDeleteTarget(target);
    setShowDeleteAlert(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteAlert(false);
    setLoading(true);

    if (deleteTarget === 'PAGE1') {
      const localIdsToDelete = [];
      const dbIdsToDelete = [];

      selectedIds.forEach((id) => {
        if (newRows.some((r) => r.id === id)) localIdsToDelete.push(id);
        else dbIdsToDelete.push(id);
      });

      if (localIdsToDelete.length > 0) {
        setNewRows((prev) => prev.filter((r) => !localIdsToDelete.includes(r.id)));
      }

      if (dbIdsToDelete.length > 0) {
        try {
          const batch = writeBatch(db);
          dbIdsToDelete.forEach((id) => {
            batch.delete(doc(db, 'broadcastCampaigns', id));
          });
          await batch.commit();
        } catch (error) {
          console.error('Deletion error:', error);
        }
      }
      setSelectedIds([]);
    } else if (deleteTarget === 'PAGE2') {
      try {
        let currentBatch = writeBatch(db);
        let count = 0;

        for (const id of page2SelectedIds) {
          currentBatch.delete(doc(db, 'uploadMailingListBroadcast', id));
          count++;

          if (count >= 490) {
            await currentBatch.commit();
            currentBatch = writeBatch(db);
            count = 0;
          }
        }

        if (count > 0) {
          await currentBatch.commit();
        }
      } catch (error) {
        console.error('Deletion error on Page 2:', error);
      }
      setPage2SelectedIds([]);
    }

    setLoading(false);
  };

  const handleSave = async () => {
    if (!checkIsDirty()) return;

    // --- PRE-SAVE VALIDATION CHECK ---
    // Prevent saving if any modified row has selected an unpublished newsfeed
    for (const row of displayRows) {
      const isNew = newRows.some((r) => r.id === row.id);
      const isEdited = !!edits[row.id];

      if ((isNew || isEdited) && row.newsfeedName) {
        const targetNewsfeed = newsfeedOptions.find((nf) => nf.name === row.newsfeedName);

        if (targetNewsfeed) {
          const isPublished = !!(
            (targetNewsfeed.pdfDownloadUrl || targetNewsfeed.pdfStoragePath) &&
            (targetNewsfeed.pdfThumbnailUrl || targetNewsfeed.thumbStoragePath) &&
            (targetNewsfeed.hiresDownloadUrl || targetNewsfeed.hiresStoragePath)
          );

          if (!isPublished) {
            setErrorMessage(
              `The newsfeed "${row.newsfeedName}" has not been published. You cannot save a campaign with an unpublished newsfeed.`,
            );
            setShowErrorAlert(true);
            return; // Abort the save process entirely
          }
        } else {
          setErrorMessage(
            `The configuration for Newsfeed "${row.newsfeedName}" could not be found.`,
          );
          setShowErrorAlert(true);
          return; // Abort the save process entirely
        }
      }
    }
    // ---------------------------------

    const batch = writeBatch(db);

    newRows.forEach((row) => {
      const newDocRef = doc(collection(db, 'broadcastCampaigns'));
      batch.set(newDocRef, {
        date: row.date,
        description: row.description || '',
        newsfeedName: row.newsfeedName || '',
        isImportList: row.isImportList || false,
        isMailingList: row.isMailingList || false,
        status: row.status || '',
      });
    });

    Object.keys(edits).forEach((id) => {
      batch.update(doc(db, 'broadcastCampaigns', id), edits[id]);
    });

    // Clear local state BEFORE committing to prevent duplicate rows from appearing
    // due to Firestore's local latency compensation onSnapshot firing
    setNewRows([]);
    setEdits({});

    await batch.commit();
  };

  const fetchTargetEmails = async (isImportList, isMailingList) => {
    let emails = new Set();
    try {
      if (isImportList) {
        const importSnap = await getDocs(collection(db, 'uploadMailingList'));
        importSnap.docs.forEach((d) => {
          if (d.data().email) emails.add(d.data().email.toLowerCase().trim());
        });
      }
      if (isMailingList) {
        const mainSnap = await getDocs(collection(db, 'mailingList'));
        mainSnap.docs.forEach((d) => {
          if (d.data().email) emails.add(d.data().email.toLowerCase().trim());
        });
      }
    } catch (err) {
      console.error('Error gathering emails:', err);
      throw new Error('Failed to gather target emails from the selected lists.');
    }
    return Array.from(emails);
  };

  const handleBroadcastRequest = async () => {
    if (checkIsDirty()) {
      setErrorMessage('Please save your changes before broadcasting.');
      setShowErrorAlert(true);
      return;
    }

    if (selectedIds.length !== 1) return;

    const currentId = selectedIds[0];
    const currentRow = dbRows.find((r) => r.id === currentId);

    if (!currentRow) return;

    if (!currentRow.newsfeedName) {
      setErrorMessage('You must select a Newsfeed to broadcast.');
      setShowErrorAlert(true);
      return;
    }

    // --- SECONDARY VALIDATION CHECK ---
    const targetNewsfeed = newsfeedOptions.find(
      (nf) => nf.name === currentRow.newsfeedName,
    );

    if (targetNewsfeed) {
      const isPublished = !!(
        (targetNewsfeed.pdfDownloadUrl || targetNewsfeed.pdfStoragePath) &&
        (targetNewsfeed.pdfThumbnailUrl || targetNewsfeed.thumbStoragePath) &&
        (targetNewsfeed.hiresDownloadUrl || targetNewsfeed.hiresStoragePath)
      );

      if (!isPublished) {
        setErrorMessage('The newsfeed you have selected has not been published');
        setShowErrorAlert(true);
        return;
      }
    } else {
      setErrorMessage(
        `The configuration for Newsfeed "${currentRow.newsfeedName}" could not be found.`,
      );
      setShowErrorAlert(true);
      return;
    }
    // ----------------------------------

    if (!currentRow.isImportList && !currentRow.isMailingList) {
      setErrorMessage(
        'You must select at least one target list (Import List or Mailing List) to broadcast to.',
      );
      setShowErrorAlert(true);
      return;
    }

    if (
      currentRow.status === 'In Process' ||
      currentRow.status === 'Awaiting Import' ||
      currentRow.status === 'Awaiting Upload' ||
      currentRow.status === 'Tick' ||
      currentRow.status === 'Success' ||
      currentRow.status === 'Complete' ||
      currentRow.status === 'Completed'
    ) {
      setErrorMessage('This campaign has already been broadcast.');
      setShowErrorAlert(true);
      return;
    }

    try {
      const targetEmails = await fetchTargetEmails(
        currentRow.isImportList,
        currentRow.isMailingList,
      );

      if (targetEmails.length === 0) {
        setErrorMessage('No valid emails were found in the selected list(s).');
        setShowErrorAlert(true);
        return;
      }

      setBroadcastDetails({
        targetCount: targetEmails.length,
        newsfeedName: currentRow.newsfeedName,
        emails: targetEmails,
        rowId: currentRow.id,
        isImportList: currentRow.isImportList, // Used to conditionally trigger delay
      });
      setShowBroadcastConfirm(true);
    } catch (err) {
      setErrorMessage(err.message);
      setShowErrorAlert(true);
    }
  };

  const executeBroadcast = async () => {
    setShowBroadcastConfirm(false);
    setIsBroadcasting(true);

    try {
      const { rowId, newsfeedName, emails, isImportList } = broadcastDetails;

      setProgressMessage(`Preparing content for ${newsfeedName}...`);
      setProgressSeverity('info');
      setShowProgressAlert(true);

      const targetNewsfeed = newsfeedOptions.find((nf) => nf.name === newsfeedName);
      if (!targetNewsfeed) {
        throw new Error(
          `The configuration for Newsfeed "${newsfeedName}" could not be found.`,
        );
      }

      let logoUrl = null;
      try {
        const logoDoc = await getDoc(doc(db, 'settings', 'MainLogo'));
        if (logoDoc.exists()) logoUrl = logoDoc.data().url;
      } catch (err) {
        console.error('Error fetching logo:', err);
      }

      let allowRowBreaks = false;
      try {
        const promoDoc = await getDoc(doc(db, 'settings', 'PromoSettings'));
        if (promoDoc.exists()) allowRowBreaks = !!promoDoc.data().allowRowBreaks;
      } catch (err) {
        console.error('Error fetching PromoSettings:', err);
      }

      const emailFromAddress = 'news@updates.loktr.net';

      let socialLinks = [];
      try {
        const socialDoc = await getDoc(doc(db, 'settings', 'SocialMediaLinks'));
        if (socialDoc.exists() && socialDoc.data().links) {
          socialLinks = socialDoc.data().links;
        }
      } catch (err) {
        console.error('Error fetching SocialMediaLinks:', err);
      }

      const querySnapshot = await getDocs(collection(db, 'markerGroups'));
      const allMarkers = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      const filteredMarkers = allMarkers.filter((marker) => {
        let vis = marker.visibility;
        if (!vis) {
          if (marker.publicFromDate && marker.publicFromDate.trim() !== '') {
            vis = `Public From ${marker.publicFromDate}`;
          } else if (marker.isPrivate) {
            vis = 'Private';
          } else {
            vis = 'Public';
          }
        }
        if (vis === 'Private' || vis === 'Restricted') return false;
        if (vis && vis.startsWith('Public From')) {
          const datePart = vis.replace('Public From', '').trim();
          const today = new Date().toISOString().split('T')[0];
          if (datePart && datePart > today) return false;
        }
        if (targetNewsfeed.dateRangeType === 'RANGE') {
          if (targetNewsfeed.dateFrom && marker.date < targetNewsfeed.dateFrom)
            return false;
          if (targetNewsfeed.dateTo && marker.date > targetNewsfeed.dateTo) return false;
        }
        if (targetNewsfeed.categoryIds && targetNewsfeed.categoryIds.length > 0) {
          if (!targetNewsfeed.categoryIds.includes(marker.categoryId)) return false;
        } else if (targetNewsfeed.categoryId && targetNewsfeed.categoryId !== 'ALL') {
          if (marker.categoryId !== targetNewsfeed.categoryId) return false;
        }
        if (targetNewsfeed.regionIds && targetNewsfeed.regionIds.length > 0) {
          if (!targetNewsfeed.regionIds.includes(marker.regionId)) return false;
        } else if (targetNewsfeed.regionId && targetNewsfeed.regionId !== 'ALL') {
          if (marker.regionId !== targetNewsfeed.regionId) return false;
        }
        if (targetNewsfeed.keywords && targetNewsfeed.keywords.trim() !== '') {
          const searchTerms = targetNewsfeed.keywords
            .toLowerCase()
            .split(/\s+/)
            .filter((k) => k);
          const markerKeywords = (marker.keywords || '').toLowerCase();
          const match = searchTerms.some((term) => markerKeywords.includes(term));
          if (!match) return false;
        }
        const hasInfoA =
          targetNewsfeed.infoFilterA && targetNewsfeed.infoFilterA.trim().length > 0;
        const hasInfoB =
          targetNewsfeed.infoFilterB && targetNewsfeed.infoFilterB.trim().length > 0;
        if (hasInfoA || hasInfoB) {
          const allItemValues = Object.values(marker)
            .filter((val) => val !== null && val !== undefined)
            .map((val) => String(val).toUpperCase())
            .join(' ');
          let passA = true;
          if (hasInfoA) {
            const termsA = targetNewsfeed.infoFilterA
              .toUpperCase()
              .split(/\s+/)
              .filter((t) => t);
            passA = termsA.some((term) => allItemValues.includes(term));
          }
          let passB = true;
          if (hasInfoB) {
            const termsB = targetNewsfeed.infoFilterB
              .toUpperCase()
              .split(/\s+/)
              .filter((t) => t);
            passB = termsB.some((term) => allItemValues.includes(term));
          }
          if (!passA || !passB) return false;
        }
        return true;
      });

      filteredMarkers.sort((a, b) => {
        const dateA = a.date;
        const dateB = b.date;
        const hasDateA = dateA && dateA.trim() !== '';
        const hasDateB = dateB && dateB.trim() !== '';
        if (hasDateA && hasDateB) {
          if (dateA < dateB) return 1;
          if (dateA > dateB) return -1;
          return 0;
        }
        if (hasDateA && !hasDateB) return -1;
        if (!hasDateA && hasDateB) return 1;
        return 0;
      });

      const limit = parseInt(targetNewsfeed.maxResults) || 1000;
      const finalMarkers = filteredMarkers.slice(0, limit);

      if (finalMarkers.length === 0) {
        setErrorMessage(
          `${newsfeedName}: No valid locations found to broadcast based on its settings.`,
        );
        setShowErrorAlert(true);
        setIsBroadcasting(false);
        setShowProgressAlert(false);
        return;
      }

      const reportData = {
        title: targetNewsfeed.name,
        subjectTitle: targetNewsfeed.subjectTitle || '',
        salutation: targetNewsfeed.salutation || '',
        emailClose: targetNewsfeed.emailClose || '',
        regionText: 'Dynamic',
        categoryText: 'Dynamic',
        dateText: 'Dynamic',
        keyword: targetNewsfeed.keywords || '',
        infoFilterA: targetNewsfeed.infoFilterA || '',
        infoFilterB: targetNewsfeed.infoFilterB || '',
        logo: logoUrl,
        photoDimension: parseInt(targetNewsfeed.photoDimension) || 50,
        items: finalMarkers,
        includeHeader: targetNewsfeed.includeHeader ?? true,
        allowRowBreaks: allowRowBreaks,
        emailFrom: emailFromAddress,
        socialLinks: socialLinks,
      };

      const pdfDownloadUrl = targetNewsfeed.pdfDownloadUrl || null;
      const thumbDownloadUrl = targetNewsfeed.pdfThumbnailUrl || null;

      const emailDataPayload = {
        ...reportData,
        pdfDownloadUrl,
        pdfThumbnailUrl: thumbDownloadUrl,
        mode: 'NEWSFEED',
        docId: targetNewsfeed.id,
        appUrl: window.location.origin,
      };

      const { htmlBody, plainTextBody } = getPortfolioEmailContent(emailDataPayload);

      setProgressMessage(`Broadcasting via Postmark...`);
      const broadcastCall = httpsCallable(functions, 'broadcastNewsfeed');

      await broadcastCall({
        from: emailFromAddress,
        emails: emails,
        subject: reportData.subjectTitle || reportData.title,
        htmlBody: htmlBody,
        textBody: plainTextBody,
      });

      // Increment the total number of broadcast emails sent for this specific newsfeed
      const newsfeedRef = doc(db, 'newsPortfolio', targetNewsfeed.id);
      await updateDoc(newsfeedRef, {
        broadcastCount: increment(emails.length),
      });

      // ONLY archive the import list into the holding pen if it was actually selected
      if (isImportList) {
        setProgressMessage(`Archiving imported mailing list...`);
        try {
          const importRef = collection(db, 'uploadMailingList');
          const importSnap = await getDocs(importRef);

          if (!importSnap.empty) {
            let importBatch = writeBatch(db);
            let count = 0;
            for (const docSnap of importSnap.docs) {
              const data = docSnap.data();
              const newRef = doc(db, 'uploadMailingListBroadcast', docSnap.id);

              importBatch.set(newRef, { ...data, status: 'Ok' });
              importBatch.delete(docSnap.ref);
              count += 2;

              if (count >= 490) {
                await importBatch.commit();
                importBatch = writeBatch(db);
                count = 0;
              }
            }
            if (count > 0) {
              await importBatch.commit();
            }
          }
        } catch (migrateErr) {
          console.error('Failed to migrate uploadMailingList:', migrateErr);
          setErrorMessage(
            `Archive failed: ${migrateErr.message}. Check your Firestore Rules for 'uploadMailingListBroadcast'.`,
          );
          setShowErrorAlert(true);
          setIsBroadcasting(false);
          setShowProgressAlert(false);
          return;
        }
      }

      setProgressMessage(
        `${newsfeedName}: Successfully broadcast to ${emails.length} users!`,
      );
      setProgressSeverity('success');

      const currentTime = new Date().toISOString();
      const campaignRef = doc(db, 'broadcastCampaigns', rowId);

      // Branch logic based on whether a holding pen delay is required
      if (isImportList) {
        await updateDoc(campaignRef, {
          status: 'In Process',
          date: currentTime,
        });

        setInProgressCampaigns((prev) => ({
          ...prev,
          [rowId]: currentTime,
        }));
      } else {
        await updateDoc(campaignRef, {
          status: 'Complete',
          date: currentTime,
        });
      }

      setSelectedIds([]);

      await new Promise((r) => setTimeout(r, 2500));
    } catch (err) {
      console.error('Broadcast failed:', err);
      setErrorMessage(`Broadcast failed: ${err.message}`);
      setShowErrorAlert(true);
    } finally {
      setIsBroadcasting(false);
      setShowProgressAlert(false);
    }
  };

  const handleUploadToMailingList = async () => {
    if (page2SelectedIds.length === 0) return;
    setLoading(true);
    try {
      const batch = writeBatch(db);

      const validStatuses = ['Ok', 'OK', 'Tick', 'Success', 'Complete', 'Completed'];

      const selectedRows = displayPage2Rows.filter((r) =>
        page2SelectedIds.includes(r.id),
      );

      let uploadedCount = 0;
      let discardedCount = 0;

      selectedRows.forEach((row) => {
        const isRowValid = validStatuses.includes(row.status);

        if (isRowValid) {
          const cleanEmail = String(row.email).toLowerCase().trim();
          if (cleanEmail) {
            const newDocRef = doc(collection(db, 'mailingList'));

            // Removed signedUp writes; only write to mailingList now
            batch.set(newDocRef, {
              userName: row.userName || '',
              companyName: row.companyName || '',
              email: cleanEmail,
              addedAt: new Date().toISOString(),
            });

            uploadedCount++;
          } else {
            discardedCount++;
          }
        } else {
          discardedCount++;
        }

        const broadcastRef = doc(db, 'uploadMailingListBroadcast', row.id);
        batch.delete(broadcastRef);
      });

      const awaitingRow = displayRows.find(
        (r) => r.status === 'Awaiting Import' || r.status === 'Awaiting Upload',
      );
      if (awaitingRow) {
        const campaignRef = doc(db, 'broadcastCampaigns', awaitingRow.id);
        batch.update(campaignRef, { status: 'Complete' });
      }

      await batch.commit();

      if (uploadedCount === 0 && discardedCount > 0) {
        setProgressMessage(
          `${discardedCount} invalid user(s) were successfully cleared.`,
        );
      } else {
        setProgressMessage(
          `Successfully transferred ${uploadedCount} user(s) to the Mailing List.${
            discardedCount > 0 ? ` (${discardedCount} invalid discarded)` : ''
          }`,
        );
      }

      setProgressSeverity('success');
      setShowProgressAlert(true);
      setPage2SelectedIds([]);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error transferring users to Mailing List:', error);
      setErrorMessage('Failed to transfer users to Mailing List.');
      setShowErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelayComplete = async (rowId) => {
    const rowRef = doc(db, 'broadcastCampaigns', rowId);
    try {
      await updateDoc(rowRef, {
        status: 'Awaiting Import',
        date: inProgressCampaigns[rowId] || new Date().toISOString(),
      });

      setInProgressCampaigns((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    } catch (e) {
      console.error(
        'Failed to automatically update broadcast row status milestone state:',
        e,
      );
    }
  };

  const executeDownload = useCallback(
    (viewMode = 'PAGE1') => {
      const rowsToExport = viewMode === 'PAGE2' ? displayPage2Rows : displayRows;
      if (rowsToExport.length === 0) return;

      const formatDate = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return isNaN(date) ? isoString : date.toLocaleDateString();
      };

      let headerRow = [];
      let dataRows = [];
      let wscols = [];
      let sheetName = '';
      let fileName = '';

      if (viewMode === 'PAGE2') {
        headerRow = ['Date', 'User Name', 'Company', 'Email', 'Status'];
        dataRows = rowsToExport.map((row) => [
          formatDate(row.addedAt || row.requestDate),
          row.userName || '',
          row.companyName || '',
          row.email || '',
          row.status || '',
        ]);
        wscols = [{ wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 35 }, { wch: 20 }];
        sheetName = 'Broadcast Details';
        fileName = `Broadcast_Details_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else {
        headerRow = [
          'Date',
          'Description',
          'Newsfeed Name',
          'Import List',
          'Mailing List',
          'Status',
        ];
        dataRows = rowsToExport.map((row) => [
          formatDate(row.date),
          row.description || '',
          row.newsfeedName || '',
          row.isImportList ? 'Yes' : 'No',
          row.isMailingList ? 'Yes' : 'No',
          row.status || '',
        ]);
        wscols = [
          { wch: 15 },
          { wch: 30 },
          { wch: 30 },
          { wch: 15 },
          { wch: 15 },
          { wch: 25 },
        ];
        sheetName = 'Broadcast Campaigns';
        fileName = `Broadcast_Campaigns_${new Date().toISOString().slice(0, 10)}.xlsx`;
      }

      const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
      worksheet['!cols'] = wscols;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });

      saveAs(data, fileName);
    },
    [displayRows, displayPage2Rows],
  );

  return {
    state: {
      displayRows,
      selectedIds,
      loading,
      newsfeedOptions,
      isBroadcasting,
      delayConfigString,
      displayPage2Rows,
      page2SelectedIds,
      page2SortConfig,
      deleteTarget,
    },
    flags: {
      isDirty: checkIsDirty(),
      canDelete: selectedIds.length > 0 || page2SelectedIds.length > 0,
      canSave: checkIsDirty(),
      canBroadcast: checkCanBroadcast(),
    },
    alerts: {
      showDeleteAlert,
      setShowDeleteAlert,
      showUnsavedAlert,
      setShowUnsavedAlert,
      showErrorAlert,
      setShowErrorAlert,
      errorMessage,
      showProgressAlert,
      setShowProgressAlert,
      progressMessage,
      progressSeverity,
      showBroadcastConfirm,
      setShowBroadcastConfirm,
      broadcastDetails,
    },
    actions: {
      handleAddNew,
      handleDeleteRequest,
      handleConfirmDelete,
      handleSave,
      handleBroadcastRequest,
      executeBroadcast,
      executeDownload,
      handleDelayComplete,
      handleUploadToMailingList,
      requestPage2Sort,
      handlePage2CheckboxChange: (id) =>
        setPage2SelectedIds((prev) =>
          prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
        ),
      handlePage2SelectAll: (checked) => {
        if (checked) {
          setPage2SelectedIds(displayPage2Rows.map((row) => row.id));
        } else {
          setPage2SelectedIds([]);
        }
      },
      handleCheckboxChange: (id) =>
        setSelectedIds((prev) =>
          prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
        ),
      handleSelectAll: (checked) => {
        if (checked) {
          setSelectedIds(displayRows.map((row) => row.id));
        } else {
          setSelectedIds([]);
        }
      },
      handleFieldChange: (id, field, val, e = null) => {
        let finalVal = val;

        if (textFormatting?.makeUppercase && e && e.nativeEvent) {
          const inputType = e.nativeEvent.inputType;
          const data = e.nativeEvent.data;

          if (inputType === 'insertFromPaste') {
            const target = e.target;
            const cursor = target.selectionStart;
            finalVal = typeof finalVal === 'string' ? finalVal.toUpperCase() : finalVal;

            setTimeout(() => {
              if (target) target.setSelectionRange(cursor, cursor);
            }, 0);
          } else if (inputType === 'insertText' && data && typeof finalVal === 'string') {
            const target = e.target;
            const cursor = target.selectionStart;
            const dataLength = data.length;

            if (cursor !== null && cursor >= dataLength) {
              const insertedStr = finalVal.slice(cursor - dataLength, cursor);
              let swapped = '';
              for (let i = 0; i < insertedStr.length; i++) {
                const char = insertedStr[i];
                swapped +=
                  char === char.toLowerCase() ? char.toUpperCase() : char.toLowerCase();
              }
              finalVal =
                finalVal.slice(0, cursor - dataLength) + swapped + finalVal.slice(cursor);

              setTimeout(() => {
                if (target) target.setSelectionRange(cursor, cursor);
              }, 0);
            }
          }
        }

        if (newRows.some((r) => r.id === id)) {
          setNewRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, [field]: finalVal } : r)),
          );
        } else {
          setEdits((prev) => ({
            ...prev,
            [id]: { ...(prev[id] || {}), [field]: finalVal },
          }));
        }
      },
      resetInterface: () => {
        setNewRows([]);
        setEdits({});
        setSelectedIds([]);
        setShowUnsavedAlert(false);
      },
    },
  };
}
